import { createInstance } from 'i18next';
import detector from 'i18next-browser-languagedetector';
import translationChs from './locales/chs/translation.json';
import translationCht from './locales/cht/translation.json';
import translationEng from './locales/eng/translation.json';

export type IdigestChatLanguage = 'eng' | 'cht' | 'chs';

/**
 * Isolated i18next instance for the chat package.
 *
 * IMPORTANT: do NOT call `initReactI18next` here. That plugin calls
 * `setI18n()` on the shared react-i18next module and would overwrite the
 * host app's (vine-web) i18n singleton — breaking host language switching.
 * Chat UI must wrap with `<I18nextProvider i18n={chatI18n}>` instead.
 */
const i18n = createInstance();

/** Host locale aliases → chat resource keys (eng/cht/chs). */
const LANGUAGE_ALIASES: Record<string, IdigestChatLanguage> = {
  eng: 'eng',
  en: 'eng',
  'en-us': 'eng',
  'en-gb': 'eng',
  us: 'eng',
  chs: 'chs',
  cn: 'chs',
  'zh-cn': 'chs',
  'zh-hans': 'chs',
  zh: 'chs',
  cht: 'cht',
  tw: 'cht',
  hk: 'cht',
  'zh-tw': 'cht',
  'zh-hk': 'cht',
  'zh-hant': 'cht'
};

export function resolveIdigestChatLanguage(lng?: string | null): IdigestChatLanguage {
  if (!lng) {
    return 'chs';
  }
  const normalized = lng.trim().toLowerCase().replace(/_/g, '-');
  if (LANGUAGE_ALIASES[normalized]) {
    return LANGUAGE_ALIASES[normalized];
  }
  const base = normalized.split('-')[0];
  return LANGUAGE_ALIASES[base] ?? 'chs';
}

/** Sync chat UI language (does not touch the host app's i18n). */
export function setIdigestChatLanguage(lng?: string | null): IdigestChatLanguage {
  const resolved = resolveIdigestChatLanguage(lng);
  if (i18n.language !== resolved) {
    void i18n.changeLanguage(resolved);
  }
  return resolved;
}

void i18n.use(detector).init({
  detection: {
    order: ['querystring', 'cookie', 'subdomain'],
    lookupQuerystring: 'lang',
    lookupCookie: 'i18next',
    caches: [],
    cookieMinutes: 10080
  },
  resources: {
    eng: { translation: translationEng },
    cht: { translation: translationCht },
    chs: { translation: translationChs }
  },
  fallbackLng: 'chs',
  supportedLngs: ['eng', 'cht', 'chs'],
  nonExplicitSupportedLngs: false,
  interpolation: {
    escapeValue: false
  }
});

export default i18n;
